import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import AcceptInvite from '@/pages/AcceptInvite';
import Dashboard from '@/pages/Dashboard';
import Portfolios from '@/pages/Portfolios';
import Properties from '@/pages/Properties';
import Units from '@/pages/Units';
import Tenants from '@/pages/Tenants';
import Leases from '@/pages/Leases';
import Payments from '@/pages/Payments';
import Maintenance from '@/pages/Maintenance';
import Tasks from '@/pages/Tasks';
import Insights from '@/pages/Insights';
import Community from '@/pages/Community';
import TenantPortal from '@/pages/TenantPortal';
import PaymentSuccess from '@/pages/PaymentSuccess';
import Settings from '@/pages/Settings';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import AIInsights from '@/pages/AIInsights';
import Marketing from '@/pages/Marketing';
import Admin from '@/pages/Admin';
import Documents from '@/pages/Documents';
import Inspections from '@/pages/Inspections';
import DigitalTwin from '@/pages/DigitalTwin';
import SmartUpload from '@/pages/SmartUpload';
import BulkImport from '@/pages/BulkImport';
import Team from '@/pages/Team';
import ExcelImport from '@/pages/ExcelImport';
import VideoStudio from '@/pages/VideoStudio';
import Marketplace from '@/pages/Marketplace';
import LeaseAbstract from '@/pages/LeaseAbstract';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import ProductPage from '@/pages/ProductPage';
import Platform from '@/pages/Platform';
import Governance from '@/pages/Governance';
import PortalLogin from '@/pages/PortalLogin';
import OwnerPortal from '@/pages/OwnerPortal';
import VendorPortal from '@/pages/VendorPortal';
import InvestorPortal from '@/pages/InvestorPortal';
import ContentPage from '@/pages/ContentPage';
import Readiness from '@/pages/Readiness';
import TenantOnboarding from '@/pages/TenantOnboarding';
import Intelligence from '@/pages/Intelligence';
import ComingSoon from '@/pages/ComingSoon';
import Onboarding from '@/pages/Onboarding';
import DecisionCenter from '@/pages/DecisionCenter';
import Listings from '@/pages/leasing/Listings';
import Prospects from '@/pages/leasing/Prospects';
import Applicants from '@/pages/leasing/Applicants';
import PublicListing from '@/pages/PublicListing';
import WorkOrder from '@/pages/WorkOrder';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
}

// Gate that requires a specific permission. Redirects to user's home if not allowed.
function RequirePerm({ perm, children }) {
  const { hasPerm, loading, user, permissions } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (!hasPerm(perm)) {
    // Tenants always go to their portal; everyone else → dashboard if available, else settings
    const fallback = permissions?.role === 'tenant' ? '/tenant' : (hasPerm('dashboard') ? '/dashboard' : '/settings');
    return <Navigate to={fallback} replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<PortalLogin />} />
        <Route path="/login/:portal" element={<PortalLogin />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/products/:slug" element={<ProductPage />} />
        <Route path="/why/:slug" element={<ContentPage kind="why" />} />
        <Route path="/resources/:slug" element={<ContentPage kind="resources" />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/dashboard" element={<ProtectedRoute><RequirePerm perm="dashboard"><Dashboard /></RequirePerm></ProtectedRoute>} />
        <Route path="/portfolios" element={<ProtectedRoute><RequirePerm perm="portfolios"><Portfolios /></RequirePerm></ProtectedRoute>} />
        <Route path="/properties" element={<ProtectedRoute><RequirePerm perm="properties"><Properties /></RequirePerm></ProtectedRoute>} />
        <Route path="/units" element={<ProtectedRoute><RequirePerm perm="units"><Units /></RequirePerm></ProtectedRoute>} />
        <Route path="/tenants" element={<ProtectedRoute><RequirePerm perm="tenants"><Tenants /></RequirePerm></ProtectedRoute>} />
        <Route path="/leases" element={<ProtectedRoute><RequirePerm perm="leases"><Leases /></RequirePerm></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute><RequirePerm perm="payments"><Payments /></RequirePerm></ProtectedRoute>} />
        <Route path="/maintenance" element={<ProtectedRoute><RequirePerm perm="maintenance"><Maintenance /></RequirePerm></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><RequirePerm perm="maintenance"><Tasks /></RequirePerm></ProtectedRoute>} />
        <Route path="/insights" element={<ProtectedRoute><RequirePerm perm="insights"><Insights /></RequirePerm></ProtectedRoute>} />
        <Route path="/community" element={<ProtectedRoute><RequirePerm perm="community"><Community /></RequirePerm></ProtectedRoute>} />
        <Route path="/ai-insights" element={<ProtectedRoute><RequirePerm perm="ai"><AIInsights /></RequirePerm></ProtectedRoute>} />
        <Route path="/marketing" element={<ProtectedRoute><RequirePerm perm="marketing"><Marketing /></RequirePerm></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><RequirePerm perm="admin"><Admin /></RequirePerm></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><RequirePerm perm="team"><Team /></RequirePerm></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><RequirePerm perm="documents"><Documents /></RequirePerm></ProtectedRoute>} />
        <Route path="/inspections" element={<ProtectedRoute><RequirePerm perm="inspections"><Inspections /></RequirePerm></ProtectedRoute>} />
        <Route path="/digital-twin" element={<ProtectedRoute><RequirePerm perm="digital_twin"><DigitalTwin /></RequirePerm></ProtectedRoute>} />
        <Route path="/smart-upload" element={<ProtectedRoute><RequirePerm perm="smart_upload"><SmartUpload /></RequirePerm></ProtectedRoute>} />
        <Route path="/bulk-import" element={<ProtectedRoute><RequirePerm perm="bulk_import"><BulkImport /></RequirePerm></ProtectedRoute>} />
        <Route path="/excel-import" element={<ProtectedRoute><RequirePerm perm="bulk_import"><ExcelImport /></RequirePerm></ProtectedRoute>} />
        <Route path="/marketplace" element={<ProtectedRoute><RequirePerm perm="maintenance"><Marketplace /></RequirePerm></ProtectedRoute>} />
        <Route path="/lease-abstract" element={<ProtectedRoute><RequirePerm perm="ai"><LeaseAbstract /></RequirePerm></ProtectedRoute>} />
        <Route path="/videos" element={<ProtectedRoute><RequirePerm perm="ai"><VideoStudio /></RequirePerm></ProtectedRoute>} />
        <Route path="/governance" element={<ProtectedRoute><RequirePerm perm="governance"><Governance /></RequirePerm></ProtectedRoute>} />
        <Route path="/platform" element={<ProtectedRoute><RequirePerm perm="platform"><Platform /></RequirePerm></ProtectedRoute>} />
        <Route path="/owner-portal" element={<ProtectedRoute><OwnerPortal /></ProtectedRoute>} />
        <Route path="/vendor-portal" element={<ProtectedRoute><VendorPortal /></ProtectedRoute>} />
        <Route path="/investor-portal" element={<ProtectedRoute><InvestorPortal /></ProtectedRoute>} />
        <Route path="/readiness" element={<ProtectedRoute><RequirePerm perm="readiness"><Readiness /></RequirePerm></ProtectedRoute>} />
        <Route path="/tenant-onboarding" element={<ProtectedRoute><RequirePerm perm="tenant_onboarding"><TenantOnboarding /></RequirePerm></ProtectedRoute>} />
        <Route path="/intelligence" element={<ProtectedRoute><RequirePerm perm="ai"><Intelligence /></RequirePerm></ProtectedRoute>} />
        <Route path="/coming-soon/*" element={<ProtectedRoute><ComingSoon /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/decisions" element={<ProtectedRoute><DecisionCenter /></ProtectedRoute>} />
        {/* Leasing Lifecycle */}
        <Route path="/listings" element={<ProtectedRoute><RequirePerm perm="leases"><Listings /></RequirePerm></ProtectedRoute>} />
        <Route path="/prospects" element={<ProtectedRoute><RequirePerm perm="leases"><Prospects /></RequirePerm></ProtectedRoute>} />
        <Route path="/applicants" element={<ProtectedRoute><RequirePerm perm="leases"><Applicants /></RequirePerm></ProtectedRoute>} />
        {/* Work Order detail */}
        <Route path="/work-orders/:wid" element={<ProtectedRoute><RequirePerm perm="maintenance"><WorkOrder /></RequirePerm></ProtectedRoute>} />
        {/* Public listing landing page (NO AUTH, NO Layout) */}
        <Route path="/l/:slug" element={<PublicListing />} />
        <Route path="/tenant" element={<ProtectedRoute><TenantPortal /></ProtectedRoute>} />
        <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
